import { SelectionModel } from "@angular/cdk/collections";
import { FlatTreeControl } from "@angular/cdk/tree";
import { DatePipe } from "@angular/common";
import { Injectable, Injector } from "@angular/core";
import { MatTreeFlattener } from "@angular/material/tree";
import { ReferencesMap } from "@orion2/importer/models";
import { DbItem } from "@orion2/models/couch.models";
import { TocItemTree, FlatTreeNode, TreeNode, TocItemType } from "@orion2/models/tocitem.models";
import { getCompactedDMC, getIncrementalPackageId } from "@orion2/utils/functions.utils";
import { TocItemService } from "@viewer/core";
import { CsvService } from "@viewer/core/csv/csv.service";
import { Charset, DownloadFileService } from "libs/download/download-file.service";
import { cloneDeep } from "lodash";
import { reaction } from "mobx";
import { v4 as uuidv4 } from "uuid";

@Injectable({
  providedIn: "root"
})
export class MaintFolderService extends TocItemService {
  protected tiType = TocItemType.MAINTENANCE_FOLDER;
  public nbrOfNodes = 0;
  public indentation = 40;
  public treeControl: FlatTreeControl<FlatTreeNode>;
  public treeFlattener: MatTreeFlattener<TreeNode, FlatTreeNode>;
  public expansionModel = new SelectionModel<string>(true);
  public selectionModel = new SelectionModel<string>(true, []);
  public maintFolders: TocItemTree[];
  public referencesMap: ReferencesMap;

  constructor(
    injector: Injector,
    private datePipe: DatePipe,
    private csvService: CsvService,
    private downloadFileService: DownloadFileService
  ) {
    super(injector);
    this.treeFlattener = this.buildTreeFlattener();
    this.treeControl = this.buildTreeControl();
    reaction(
      () => ({
        pubId: this.store.publicationID,
        pubRev: this.store.publicationRevision
      }),
      (state: { pubId: string; pubRev: string }) => {
        if (state.pubId && state.pubRev) {
          // SPEC: Reset references map when changing pub
          this.setReferencesMap();
        }
      },
      { fireImmediately: true }
    );
  }

  public setIndentation() {
    this.indentation = window.innerWidth < 500 ? 20 : 40;
  }

  public buildTreeFlattener(): MatTreeFlattener<TreeNode, FlatTreeNode> {
    return new MatTreeFlattener(
      (node: TreeNode, level: number) => ({
        id: node.id,
        name: node.name,
        dmc: node.dmc,
        expandable: !!node.children?.length,
        level,
        treeId: node.treeId,
        parentId: node.parentId
      }),
      (node: FlatTreeNode) => node.level,
      (node: FlatTreeNode) => node.expandable,
      (node: TreeNode) => node.children
    );
  }

  public buildTreeControl(): FlatTreeControl<FlatTreeNode> {
    return new FlatTreeControl<FlatTreeNode>(
      (node: FlatTreeNode) => node.level,
      (node: FlatTreeNode) => node.expandable
    );
  }

  public generateTree(key: string, depth: number, maxElements: number): TocItemTree {
    this.nbrOfNodes = 0;
    return {
      _id: `${this.tiType}_${uuidv4()}__${this.store.pubInfo.revision}`,
      data: [
        {
          id: `node_${uuidv4().split("-").pop()}`,
          dmc: key,
          children: this.generateLevel(key, depth, maxElements)
        }
      ],
      type: this.tiType,
      minRevision: this.store.pubInfo.revision
    };
  }

  public generateLevel(key: string, depth: number, maxElements: number): TreeNode[] {
    if (depth === 0 || !this.referencesMap?.[key] || this.nbrOfNodes > maxElements) {
      return [];
    }

    const childKeys = this.referencesMap?.[key] || [];

    return childKeys.map((childKey: string) => {
      this.nbrOfNodes++;

      return {
        id: `node_${uuidv4().split("-").pop()}`,
        dmc: childKey,
        children: this.generateLevel(childKey, depth - 1, maxElements)
      };
    });
  }

  /**
   * Select or deselect all descendants of node
   */
  public toggleDescendantSelection(node: FlatTreeNode, select: boolean): void {
    const funcName = select ? "select" : "deselect";
    this.treeControl
      .getDescendants(node)
      .forEach((descendant: FlatTreeNode) => this.selectionModel[funcName](descendant.id));
  }

  /**
   * Expand node after update data (delete)
   */
  public reExpand() {
    this.expansionModel.selected.forEach((id: string) => {
      const nodeToExpand = this.treeControl.dataNodes.find((node: FlatTreeNode) => node.id === id);
      this.treeControl.expand(nodeToExpand);
    });
  }

  /**
   * Download a zipped CSV containing all selected nodes
   */
  public exportNodes(nodes: FlatTreeNode[]): Promise<void> {
    const zipFilename = this.getFilename();
    return this.downloadFileService.download([this.createCSV(nodes)], zipFilename, null, true);
  }

  /**
   * The goal is to delete only the parent nodes that are selected because deleting
   * a parent node inherently deletes all of its children.
   *
   * @param nodes The list of FlatTreeNodes.
   */
  public deleteNodes(nodes: FlatTreeNode[]): Promise<TocItemTree[]> {
    const minimalNodesToDelete = this.getMinimalNodesToDelete(nodes);
    const oldDocs = cloneDeep(this.maintFolders);
    const newDocs = cloneDeep(oldDocs);
    const docsToDelete = [];
    const docsToUpdate = [];

    minimalNodesToDelete.forEach((nodeToDelete: FlatTreeNode) => {
      // parent of nodeToDelete should be update (delete the children nodeToDelete)
      if (nodeToDelete.parentId) {
        const parent = this.flattenTree(newDocs.flatMap((newDoc: TocItemTree) => newDoc.data)).find(
          (flatNode: TreeNode) => flatNode.id === nodeToDelete.parentId
        );
        const index = parent.children.map((child: TreeNode) => child.id).indexOf(nodeToDelete.id);
        parent.children.splice(index, 1);
        if (!docsToUpdate.includes(nodeToDelete.treeId)) {
          docsToUpdate.push(nodeToDelete.treeId);
        }
      } else {
        // it's a root node so the document should be delete
        const index = newDocs.findIndex(
          (maintFolder: TocItemTree) => maintFolder._id === nodeToDelete.treeId
        );
        const [maintFolderToDelete] = newDocs.splice(index, 1);
        docsToDelete.push(maintFolderToDelete);
      }
    });

    const updatePromises = docsToUpdate.map((docToUpdate: string) => {
      const oldDoc = oldDocs.find((doc: TocItemTree) => doc._id === docToUpdate);
      const newDoc = newDocs.find((doc: TocItemTree) => doc._id === docToUpdate);
      return this.update(oldDoc, newDoc);
    });

    const deletePromises = docsToDelete.map((doc: TocItemTree) => {
      return this.delete(doc);
    });

    return Promise.all([...updatePromises, ...deletePromises]).then(() => newDocs);
  }

  /**
   * Filter nodes to have only common ancestor
   * if nodes contains a parent and its childrens, we keep only the parent
   * @param nodes The list of FlatTreeNodes.
   * @returns A filtered list of minimal nodes that should be deleted.
   */
  private getMinimalNodesToDelete(nodes: FlatTreeNode[]): FlatTreeNode[] {
    const nodesSet = new Set(nodes);
    const nodesToDelete = [];
    while (nodesSet.size > 0) {
      const currentNode = nodesSet.values().next().value;
      nodesToDelete.push(currentNode);
      nodesSet.delete(currentNode);
      const descendants = this.treeControl.getDescendants(currentNode);
      descendants.forEach((descendant: FlatTreeNode) => nodesSet.delete(descendant));
    }
    return nodesToDelete;
  }

  /** Flat TreeNode childrens */
  private flattenTree(nodes: TreeNode[]): TreeNode[] {
    return nodes.reduce(
      (flatTree: TreeNode[], node: TreeNode) =>
        flatTree.concat(node, this.flattenTree(node.children)),
      []
    );
  }

  /**
   * construct the CSV File with theses informations:
   * compacted dmc, title, package id, publication language, user id, and level of the nodes
   * @param nodes The list of FlatTreeNodes.
   * @returns The CSV File
   */
  private createCSV(nodes: FlatTreeNode[]): File {
    const csvFilename = `${this.getFilename()}.csv`;

    const headers = ["DMC", "Title", "IdPackage", "IdLanguage", "Login", "Level"];

    const rows = nodes.map((node: FlatTreeNode) => [
      getCompactedDMC(node.dmc),
      this.store.getDUMeta(node.dmc).shortTitle,
      this.store.pubInfo.packageId,
      this.store.pubInfo.lang,
      this.store.user.samAccountName,
      (node.level + 1).toString()
    ]);

    const csvString = this.csvService.createCsvString(headers, rows);
    const blob = new Blob(["\uFEFF" + csvString], { type: Charset.CSV_UTF8 });
    return new File([blob], csvFilename);
  }

  private getFilename(): string {
    return [
      "FOLDERS",
      getIncrementalPackageId(this.store.publicationID),
      this.store.publicationRevision,
      this.datePipe.transform(new Date(), "yyyy_MM_dd_HH_mm_ss"),
      this.store.user.samAccountName
    ].join("_");
  }

  /**
   * References map is used for generate maintenance folders
   * If the capability is false or the document is not present in DB,
   * we set referencesMap to undefined for prevent creation of maintenance folders
   */
  public setReferencesMap(): void {
    if (!this.store?.pubInfo?.capabilities?.maintFolder) {
      this.referencesMap = undefined;
    } else {
      this.pouchService.xmlCaller
        .get("references_map")
        .then((doc: DbItem) => {
          this.referencesMap = doc?.["data"];
        })
        .catch((error: Error) => {
          console.error("setReferencesMap", error);
        });
    }
  }
}
